import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { NewInput } from '../../../../Components/PIM/common';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';
import useDeferredRowChange from '../../../../Components/BombiixAGGrid/useDeferredRowChange';
import type { BombiixCellRendererParams, BombiixRowId } from '../../../../Components/BombiixAGGrid';
import { ProductItem, TableRow } from './types';

const MAX_LENGTH = 200;

interface DescAutoFillExtras {
  /** Matching products to offer as an autocomplete dropdown (draft/add row only). */
  suggestions?: ProductItem[];
  /** Pick a suggestion → auto-fill the row from that product. */
  onSelectSuggestion?: (product: ProductItem, row?: TableRow) => void;
  /** With several drafts open, only the draft the suggestions were searched for shows them. */
  suggestionRowId?: string;
}

/** Split a description into its distinct, non-empty words (used as tag names). */
const descriptionWords = (description: string): string[] =>
  description
    .split(' ')
    .map((word) => word.trim())
    .filter(Boolean);

/** Parse the row's comma-joined tags string into a de-duplicated list. */
const parseTags = (tags: unknown): string[] =>
  typeof tags === 'string'
    ? tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : [];

type DescriptionCellProps = BombiixCellRendererParams<TableRow, string> & DescAutoFillExtras;

/**
 * Product Description cell for the table view inline row. Mirrors the CreateProduct modal:
 * a text input capped at 200 chars with a live `length/200` counter, plus a checkbox that
 * pushes the description's words into the row's Tags (and removes them again when unticked).
 * List rows render read-only via the column's default accessor, so this renderer is only
 * mounted on the active add/edit row.
 */
function DescriptionCell(params: DescriptionCellProps) {
  const { value, data, colDef, onRowChange, getRowId, placeholder, disabled, readOnly, error, suggestions = [], onSelectSuggestion, suggestionRowId } =
    params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;

  const focusedRef = useRef<boolean>(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [local, setLocal] = useState<string>(value ?? '');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  /* Optimistic mirror of the "add as tags" flag. Clicking the checkbox makes it the active
     INPUT inside the Description cell, so BombiixAGGrid's caret-preserving refresh deliberately
     skips this column — reading data.tagDescription straight left the tick one render stale (it took
     two clicks to toggle). Local state, updated in the handler and re-synced whenever data's own
     flag changes, keeps the tick correct on the first click (same pattern as ToggleCell). */
  const [asTags, setAsTags] = useState<boolean>(Boolean(data?.tagDescription));
  const dataAsTags = Boolean(data?.tagDescription);

  useEffect(() => {
    setAsTags(dataAsTags);
  }, [dataAsTags]);

  /* Autocomplete is only for the inline ADD (draft) row — mirrors ProductModal's !isProductEdit. */
  const isDraft = Boolean(data?.__isDraft) && (!suggestionRowId || data?._id === suggestionRowId);
  const showSuggestions = isDraft && dropdownOpen && local.trim().length > 0 && suggestions.length > 0;

  /* The dropdown is portalled to <body> (AG Grid cells clip overflow); anchor it under the input. */
  useEffect(() => {
    if (!showSuggestions || !wrapRef.current) return undefined;
    const measure = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (rect) setMenuRect({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [showSuggestions]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value.slice(0, MAX_LENGTH).trimStart();
    setLocal(nextValue);
    setDropdownOpen(true);
    if (rowId !== undefined && field) commitChange(rowId, field, nextValue);
  };

  const handlePickSuggestion = (product: ProductItem) => {
    setDropdownOpen(false);
    focusedRef.current = false;
    onSelectSuggestion?.(product, data);
  };

  /**
   * Toggle "add description as tags". When checked, union the description's words into the
   * existing tags; when unchecked, drop exactly those words. The flag itself is persisted on
   * the row (`tagDescription`, the field the product stores) so the tick survives a save.
   */
  const handleToggleTags = (event: ChangeEvent<HTMLInputElement>) => {
    if (rowId === undefined) return;
    const {checked} = event.target;
    setAsTags(checked);
    /*
     * Blur so the checkbox isn't left as the grid's focused INPUT (which would make BombiixAGGrid
     * skip refreshing this cell to preserve a caret it doesn't have).
     */
    event.target.blur();
    const words = descriptionWords(local);
    const current = parseTags(data?.tags);
    const nextTags = checked
      ? Array.from(new Set([...current, ...words]))
      : current.filter((tag) => !words.includes(tag));
    onRowChange?.(rowId, 'tags', nextTags.join(', '));
    onRowChange?.(rowId, 'tagDescription', checked);
  };

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div ref={wrapRef} className="cag-cell ptv-description-cell position-relative">
        <NewInput
          className="ptv-desc-input"
          type="text"
          maxLength={MAX_LENGTH}
          invalid={Boolean(error)}
          value={local}
          placeholder={placeholder ?? colDef?.headerName ?? 'Description'}
          disabled={disabled}
          readOnly={readOnly}
          startAdornment={
            <NewCheckbox
              size="sm"
              checked={asTags}
              disabled={disabled || readOnly}
              onChange={handleToggleTags}
            />
          }
          endAdornment={
            <span className="bombiix-input__counter">
              {local.length}/{MAX_LENGTH}
            </span>
          }
          onFocus={() => {
            focusedRef.current = true;
            if (isDraft && local.trim().length > 0) setDropdownOpen(true);
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
            setLocal(value ?? '');
            /* Delay so a suggestion click (mousedown → click) registers before the menu unmounts. */
            setTimeout(() => setDropdownOpen(false), 150);
          }}
          onChange={handleChange}
        />
        {showSuggestions &&
          menuRect &&
          typeof document !== 'undefined' &&
          createPortal(
            <ul className="ptv-desc-suggestions" style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}>
              {suggestions.map((product) => (
                <li key={product._id}>
                  <button type="button" className="ptv-desc-suggestion" onMouseDown={() => handlePickSuggestion(product)}>
                    {product.number ? <span className="ptv-desc-suggestion-number">{product.number}</span> : null}
                    <span className="ptv-desc-suggestion-desc">{product.description}</span>
                  </button>
                </li>
              ))}
            </ul>,
            document.body,
          )}
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default DescriptionCell;
